'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import type { ServiceAreaData } from '../../../data';

const SECTION_ID = 'related-towns';

interface RelatedTownsProperties {
  currentSlug: string;
  nearbyTowns: string[];
  dataMap: Record<string, ServiceAreaData>;
}

function RelatedTowns({
  currentSlug,
  nearbyTowns,
  dataMap,
}: RelatedTownsProperties) {
  const towns = nearbyTowns
    .filter((slug) => slug !== currentSlug && dataMap[slug])
    .map((slug) => dataMap[slug]);

  if (towns.length === 0) {
    return null;
  }

  return (
    <SectionBase id={SECTION_ID} background="bg-background">
      <div className="flex flex-col items-center gap-10">
        <SectionHeader
          subtitle="NEARBY AREAS"
          title="We Also Serve These Communities"
          description="Cornerstone Homes provides custom home building and renovation services across northeastern Oklahoma."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          {towns.map((town) => (
            <Link
              key={town.slug}
              href={`/about/our-service-areas/${town.slug}`}
              className="group flex items-center justify-between gap-3 p-5 rounded-lg bg-card hover:bg-primary/5 border border-border transition-colors"
            >
              <div className="flex flex-col gap-1">
                <span className="font-prata font-semibold text-foreground group-hover:text-primary transition-colors">
                  {town.name}
                </span>
                <span className="text-sm text-muted-foreground">
                  {town.tagline}
                </span>
              </div>
              <ArrowRight className="size-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </Link>
          ))}
        </div>

        <Link
          href="/about/our-service-areas"
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors underline underline-offset-4"
        >
          View All Service Areas
        </Link>
      </div>
    </SectionBase>
  );
}

export default RelatedTowns;
